import scrollService, { refreshScroll } from '../core/ScrollService.js';

const STORAGE_KEY = 'partenaire_annuaire_filters';
const FILTER_GROUPS = ['certification', 'expertise', 'modalite', 'region', 'taille', 'secteur'];

const normalizeRechercheForModal = (value) => {
    const parts = Array.isArray(value) ? value : String(value || '').split(',');
    const terms = [];
    const seen = new Set();
    parts.forEach((part) => {
        const term = String(part || '').trim();
        if (!term) {
            return;
        }
        const key = term.toLowerCase();
        if (seen.has(key)) {
            return;
        }
        seen.add(key);
        terms.push(term);
    });
    return terms;
};

let modalBound = false;
let currentPartnerName = '';
let criteriaProvider = null;

const getModal = () => document.querySelector('[data-partner-contact-modal]');

const getPartnerField = () => {
    const modal = getModal();
    return modal ? modal.querySelector('[data-partner-field="partner_id"]') : null;
};

const getPartnerNameField = () => {
    const modal = getModal();
    return modal ? modal.querySelector('[data-partner-field="partner_name"]') : null;
};

const getCriteriaField = () => {
    const modal = getModal();
    return modal ? modal.querySelector('[data-partner-field="criteria"]') : null;
};

/**
 * Enrichit les filtres (slugs) avec un map labels pour pills / email.
 */
const enrichCriteriaWithLabels = (filters = {}, root = document) => {
    const criteria = {
        recherche: normalizeRechercheForModal(filters.recherche),
        labels: {},
    };

    FILTER_GROUPS.forEach((group) => {
        const values = Array.isArray(filters[group]) ? filters[group] : [];
        criteria[group] = [...values];
        criteria.labels[group] = {};

        values.forEach((slug) => {
            if (!slug) {
                return;
            }
            const input = root.querySelector(
                `[data-partners-filter][data-filter-group="${group}"][data-filter-value="${CSS.escape(slug)}"]`
            );
            const label = input?.getAttribute('data-filter-label')
                || input?.closest('label')?.querySelector('.partnersAnnuaireFilterOption__text')?.textContent?.trim()
                || slug;
            criteria.labels[group][slug] = label;
        });
    });

    return criteria;
};

const FILTER_GROUP_LABELS = {
    certification: 'Certification',
    expertise: 'Expertises spécifiques',
    modalite: 'Modalité',
    region: 'Zone géographique',
    taille: 'Taille entreprise',
    secteur: 'Secteur couvert',
};

const getFilterGroupLabel = (group, root = document) => {
    const legend = root.querySelector(
        `.partnersAnnuaireFilterGroup[data-filter-group="${group}"] .partnersAnnuaireFilterGroup__title`
    );
    const fromDom = legend?.textContent?.trim();
    if (fromDom) {
        return fromDom;
    }
    return FILTER_GROUP_LABELS[group] || group.charAt(0).toUpperCase() + group.slice(1);
};

const formatCriteriaList = (criteria = {}, root = document) => {
    const groups = [];
    if (Array.isArray(criteria.recherche) ? criteria.recherche.length : criteria.recherche) {
        const searchLabel = root.querySelector('[data-partners-annuaire-search] .partnersAnnuaireToolbar__searchLabel')
            ?.textContent?.trim()
            || root.querySelector('[data-partners-annuaire-search] .hubFilterBar__searchLabel')
                ?.textContent?.trim()
            || 'recherche';
        groups.push({
            label: searchLabel,
            values: normalizeRechercheForModal(criteria.recherche),
        });
    }
    FILTER_GROUPS.forEach((group) => {
        const values = (criteria[group] || [])
            .map((value) => criteria.labels?.[group]?.[value] || value)
            .filter(Boolean);
        if (!values.length) {
            return;
        }
        groups.push({
            label: getFilterGroupLabel(group, root),
            values,
        });
    });
    return groups;
};

const clearFormErrors = (form) => {
    if (!form) {
        return;
    }
    form.querySelectorAll('.partnerContactModal__fieldError').forEach((el) => {
        el.textContent = '';
        el.setAttribute('hidden', 'true');
    });
    form.querySelectorAll('.is-invalid').forEach((el) => {
        el.classList.remove('is-invalid');
    });
    const formError = form.querySelector('[data-partner-contact-form-error]');
    if (formError) {
        formError.textContent = '';
        formError.setAttribute('hidden', 'true');
    }
};

const showFormErrors = (form, fields = {}, message = '') => {
    if (!form) {
        return;
    }
    clearFormErrors(form);

    Object.keys(fields).forEach((name) => {
        const errorEl = form.querySelector(`[data-error-for="${name}"]`);
        const input = form.querySelector(`[name="${name}"]`);
        if (errorEl) {
            errorEl.textContent = fields[name];
            errorEl.removeAttribute('hidden');
        }
        if (input) {
            input.classList.add('is-invalid');
            const consentWrap = input.closest('.partnerContactModal__consent');
            if (consentWrap) {
                consentWrap.classList.add('is-invalid');
            }
        }
    });

    if (message) {
        const formError = form.querySelector('[data-partner-contact-form-error]');
        if (formError) {
            formError.textContent = message;
            formError.removeAttribute('hidden');
        }
    }
};

const updateCriteriaDisplay = (criteria = {}) => {
    const modal = getModal();
    if (!modal) {
        return;
    }

    const list = modal.querySelector('[data-partner-contact-criteria-list]');
    const wrap = modal.querySelector('[data-partner-contact-criteria]');
    const groups = formatCriteriaList(criteria, document);

    if (list) {
        list.innerHTML = '';
        groups.forEach((group) => {
            const li = document.createElement('li');
            li.className = 'partnerContactModal__criteriaGroup';

            const labelEl = document.createElement('span');
            labelEl.className = 'partnerContactModal__criteriaGroupLabel';
            labelEl.textContent = group.label;
            li.appendChild(labelEl);

            const pills = document.createElement('ul');
            pills.className = 'partnerContactModal__criteriaPills';
            group.values.forEach((value) => {
                const pill = document.createElement('li');
                pill.className = 'partnerContactModal__criteriaPill';
                pill.textContent = value;
                pills.appendChild(pill);
            });
            li.appendChild(pills);
            list.appendChild(li);
        });
    }

    if (wrap) {
        if (groups.length) {
            wrap.removeAttribute('hidden');
        } else {
            wrap.setAttribute('hidden', 'true');
        }
    }

    const criteriaField = getCriteriaField();
    if (criteriaField) {
        criteriaField.value = JSON.stringify(criteria);
    }
};

const updatePartnerDisplay = (partnerId, partnerName, partnerLogoUrl = '') => {
    const modal = getModal();
    if (!modal) {
        return;
    }

    currentPartnerName = partnerName || '';

    const nameEl = modal.querySelector('[data-partner-contact-name]');
    if (nameEl) {
        nameEl.textContent = partnerName || '';
    }

    const logoEl = modal.querySelector('[data-partner-contact-logo]');
    if (logoEl) {
        logoEl.innerHTML = '';
        if (partnerLogoUrl) {
            const img = document.createElement('img');
            img.src = partnerLogoUrl;
            img.alt = partnerName || '';
            img.loading = 'lazy';
            img.decoding = 'async';
            logoEl.appendChild(img);
        }
    }

    const partnerWrap = modal.querySelector('[data-partner-contact-partner]');
    if (partnerWrap) {
        if (partnerName || partnerLogoUrl) {
            partnerWrap.removeAttribute('hidden');
        } else {
            partnerWrap.setAttribute('hidden', 'true');
        }
    }

    const partnerField = getPartnerField();
    if (partnerField) {
        partnerField.value = partnerId || '';
    }

    const partnerNameField = getPartnerNameField();
    if (partnerNameField) {
        partnerNameField.value = partnerName || '';
    }
};

const showConfirmation = (message) => {
    const modal = getModal();
    if (!modal) {
        return;
    }

    const body = modal.querySelector('[data-partner-contact-body]');
    const confirmation = modal.querySelector('[data-partner-contact-confirmation]');

    modal.classList.add('is-confirmed');
    if (body) {
        body.setAttribute('hidden', 'true');
    }
    if (confirmation) {
        confirmation.removeAttribute('hidden');
        const messageEl = confirmation.querySelector('[data-partner-contact-confirmation-message]');
        if (messageEl) {
            if (message) {
                messageEl.textContent = message;
            } else {
                const namedTpl = modal.getAttribute('data-confirmation-named') || '';
                const name = String(currentPartnerName || '').trim() || 'le partenaire';
                messageEl.textContent = namedTpl.includes('%s')
                    ? namedTpl.replace('%s', name)
                    : (messageEl.textContent || '');
            }
        }
    }

    refreshScroll(modal);
};

const resetConfirmation = () => {
    const modal = getModal();
    if (!modal) {
        return;
    }

    const body = modal.querySelector('[data-partner-contact-body]');
    const confirmation = modal.querySelector('[data-partner-contact-confirmation]');

    modal.classList.remove('is-confirmed');
    if (body) {
        body.removeAttribute('hidden');
    }
    if (confirmation) {
        confirmation.setAttribute('hidden', 'true');
    }
};

const closePartnerContactModal = () => {
    const modal = getModal();
    if (!modal) {
        return;
    }

    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    modal.setAttribute('hidden', 'true');
    document.documentElement.classList.remove('partnerContactModal-open');
    scrollService.start();
    resetConfirmation();
};

const resolveCriteria = (criteria) => {
    if (criteria !== null && criteria !== undefined) {
        return criteria;
    }
    if (typeof criteriaProvider === 'function') {
        try {
            return criteriaProvider() || {};
        } catch (e) {
            // ignore
        }
    }
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : {};
    } catch (e) {
        return {};
    }
};

const openPartnerContactModal = ({ partnerId = '', partnerName = '', partnerLogoUrl = '', criteria = null } = {}) => {
    const modal = getModal();
    if (!modal) {
        console.warn('[silae] Modale contact partenaire introuvable.');
        return;
    }

    resetConfirmation();
    updatePartnerDisplay(partnerId, partnerName, partnerLogoUrl);

    const form = modal.querySelector('[data-partner-contact-form]');
    if (form) {
        clearFormErrors(form);
        form.querySelectorAll('input:not([type="hidden"]):not([name="website_url"]), select, textarea').forEach((el) => {
            if (el.type === 'checkbox') {
                el.checked = false;
            } else {
                el.value = '';
            }
        });
        updatePartnerDisplay(partnerId, partnerName, partnerLogoUrl);
    }

    updateCriteriaDisplay(resolveCriteria(criteria) || {});

    modal.removeAttribute('hidden');
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('partnerContactModal-open');
    scrollService.stop();
    refreshScroll(modal);
};

const getAjaxConfig = () => {
    const vars = typeof partnerContactVars !== 'undefined' ? partnerContactVars : null;
    if (vars && vars.ajaxUrl) {
        return vars;
    }
    return {
        ajaxUrl: (typeof ajaxVars !== 'undefined' && ajaxVars.url) ? ajaxVars.url : '/wp-admin/admin-ajax.php',
        nonce: '',
        action: 'partenaire_contact_submit',
        i18n: {},
    };
};

const getSubmitLabel = (btn) => {
    if (!btn) {
        return '';
    }
    const span = btn.querySelector('span');
    return (span ? span.textContent : btn.textContent || '').trim();
};

const setSubmitLabel = (btn, label) => {
    if (!btn || !label) {
        return;
    }
    const span = btn.querySelector('span');
    if (span) {
        span.textContent = label;
    } else {
        btn.textContent = label;
    }
};

const submitPartnerContactForm = async (form) => {
    const config = getAjaxConfig();
    const submitBtn = form.querySelector('[data-partner-contact-submit]');
    const defaultLabel = getSubmitLabel(submitBtn);
    const sendingLabel = config.i18n?.sending || '';

    clearFormErrors(form);

    if (submitBtn) {
        submitBtn.disabled = true;
        if (sendingLabel) {
            setSubmitLabel(submitBtn, sendingLabel);
        }
    }

    const body = new FormData(form);
    body.set('action', config.action || 'partenaire_contact_submit');
    body.set('nonce', config.nonce || '');

    try {
        const response = await fetch(config.ajaxUrl, {
            method: 'POST',
            body,
            credentials: 'same-origin',
        });
        const payload = await response.json().catch(() => ({}));

        if (!response.ok || !payload.success) {
            const data = payload.data || {};
            showFormErrors(
                form,
                data.fields || {},
                data.message || config.i18n?.genericError || ''
            );
            return;
        }

        showConfirmation(payload.data?.message || '');
    } catch (e) {
        showFormErrors(form, {}, config.i18n?.genericError || '');
    } finally {
        if (submitBtn) {
            submitBtn.disabled = false;
            if (defaultLabel) {
                setSubmitLabel(submitBtn, defaultLabel);
            }
        }
    }
};

const bindPartnerContactModal = (options = {}) => {
    if (typeof options.getCriteria === 'function') {
        criteriaProvider = options.getCriteria;
    }

    if (modalBound) {
        return;
    }
    modalBound = true;

    document.addEventListener('click', (e) => {
        const openBtn = e.target.closest('[data-partner-contact-open]');
        if (openBtn) {
            e.preventDefault();
            openPartnerContactModal({
                partnerId: openBtn.getAttribute('data-partner-id') || '',
                partnerName: openBtn.getAttribute('data-partner-name') || '',
                partnerLogoUrl: openBtn.getAttribute('data-partner-logo-url') || '',
                criteria: typeof criteriaProvider === 'function' ? criteriaProvider() : null,
            });
            return;
        }

        const closeBtn = e.target.closest('[data-partner-contact-close]');
        if (closeBtn) {
            e.preventDefault();
            closePartnerContactModal();
        }
    });

    document.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-partner-contact-form]');
        if (!form) {
            return;
        }
        e.preventDefault();
        submitPartnerContactForm(form);
    });
};

const persistFiltersToStorage = (filters, root = document) => {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(enrichCriteriaWithLabels(filters, root)));
    } catch (err) {
        // sessionStorage indisponible
    }
};

const appendFromAnnuaireParam = (href, fromUrl) => {
    try {
        const url = new URL(href, window.location.origin);
        url.searchParams.set('from_annuaire', fromUrl);
        return url.href;
    } catch (e) {
        return href;
    }
};

export {
    bindPartnerContactModal,
    openPartnerContactModal,
    closePartnerContactModal,
    enrichCriteriaWithLabels,
    persistFiltersToStorage,
    appendFromAnnuaireParam,
    STORAGE_KEY as PARTNER_FILTERS_STORAGE_KEY,
};
